<template>
  <div>
    <div v-for="item in perms.groups" :key="item.full_key" class="pl-2">
      <div class="permission-group-row">
        <CButton 
          @click="toggleGroup(item.full_key)" 
          class="permission-expand-button" 
          :title="isExpanded(item.full_key) ? 'Comprimi' : 'Espandi'"
          variant="ghost">
          <CIcon :name="isExpanded(item.full_key) ? 'cilMinus' : 'cilPlus'"
                 class="toggle-icon"
                 size="sm"/>
        </CButton>
        <CButton 
          @click="onClickGroup(item.full_key)" 
          class="permission-item-button" 
          :disabled="!item.editable"
          variant="ghost">
          <div class="permission-status-indicator" :class="item.icon.class"></div>
          <span class="permission-label" :class="{ 'is-highlighted': item.isHighlighted }">{{ item.label }}</span>
          <span v-if="breadcrumbPath" class="permission-breadcrumb">{{ breadcrumbPath }}</span>
        </CButton>
      </div>
      <div v-if="isExpanded(item.full_key)" class="permission-sub-items">
        <Permission 
          :permissions="item.perms" 
          :active="item.active" 
          :prefix="item.prefix" 
          :level="level + 1" 
          :parent-breadcrumb="buildBreadcrumb(item.label)"
          :search-filter="searchFilter"
          @click="onClickSub($event)"
        />
      </div>
    </div>
    <div v-for="item in perms.perms" :key="item.full_key" class="pl-2">
      <div class="permission-group-row">
        <div class="permission-expand-spacer"></div>
        <CButton 
          @click="onClickPermission(item.full_key)" 
          class="permission-item-button"
          :disabled="!item.editable"
          variant="ghost">
          <div class="permission-status-indicator" :class="item.icon.class"></div>
          <span class="permission-label" :class="{ 'is-highlighted': item.isHighlighted }">{{ item.label }}</span>
          <span v-if="breadcrumbPath" class="permission-breadcrumb">{{ breadcrumbPath }}</span>
        </CButton>
      </div>
    </div>
  </div>
</template>

<script>
import Permission from "@/domain/roles/components/Permission.vue";

export default {
  name: "Permission",
  emits: ['click'],
  components: {
    Permission
  },
  props: {
    permissions: {
      type: Object,
      required: true
    },
    active: {
      type: [Object, String],
      required: false
    },
    prefix: {
      type: String,
      required: false,
      default: ''
    },
    level: {
      type: Number,
      required: false,
      default: 0
    },
    parentBreadcrumb: {
      type: String,
      required: false,
      default: ''
    },
    searchFilter: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      expandedGroups: {}
    }
  },
  computed: {
    breadcrumbPath() {
      if (this.level === 0 || !this.parentBreadcrumb) return ''
      // Limita la lunghezza per evitare collisioni su livelli profondi
      if (this.level > 2 && this.parentBreadcrumb.length > 20) {
        const parts = this.parentBreadcrumb.split(' > ')
        return parts.length > 2 ? `...${parts.slice(-1)[0]}` : this.parentBreadcrumb
      }
      return this.parentBreadcrumb
    },
    perms() {
      const keys = Object.keys(this.permissions)
      return keys.reduce((acc, key) => {
        const full_key = `${this.prefix}${key}`
        const label = this.getLabel(key)
        
        if (this.permissions[key] === true || this.permissions[key] === '*') {
          // È un permesso finale o tutti i permessi del gruppo
          if (this.matchesSearch(label, key)) {
            acc.perms.push({
              key: key,
              full_key: full_key,
              icon: this.getIcon(key),
              label: label,
              editable: this.active !== '*',
              isHighlighted: this.searchFilter && this.matchesSearch(label, key)
            })
          }
        } else if (typeof this.permissions[key] === 'object' && this.permissions[key] !== null) {
          // È un gruppo con sotto-permessi
          const hasMatches = this.matchesSearch(label, key) || this.hasMatchingChildren(this.permissions[key], `${this.prefix}${key}.`)
          
          if (hasMatches) {
            let groupActive = null
            if (this.active === '*')
              groupActive = '*'
            else if (this.active && this.active[key])
              groupActive = this.active[key]

            const groupData = {
              key: key,
              full_key: full_key,
              icon: this.getIcon(key),
              label: label,
              active: groupActive,
              perms: this.permissions[key],
              prefix: `${this.prefix}${key}.`,
              editable: this.active !== '*',
              isHighlighted: this.searchFilter && this.matchesSearch(label, key)
            }
            
            // Auto-expand groups with active permissions
            if (!this.expandedGroups.hasOwnProperty(full_key)) {
              const shouldExpand = groupActive && (
                groupActive === '*' || 
                this.hasActiveChildren(groupActive)
              );
              this.$set(this.expandedGroups, full_key, shouldExpand);
            }
            
            acc.groups.push(groupData)
          }
        }
        return acc
      }, {perms: [], groups: []})
    }
  },
  watch: {
    searchFilter: {
      handler(newFilter) {
        if (newFilter && this.perms.groups) {
          // Auto-espandi i gruppi che contengono risultati quando si effettua una ricerca
          this.perms.groups.forEach(group => {
            if (this.hasMatchingChildren(group.perms, group.prefix)) {
              this.$set(this.expandedGroups, group.full_key, true);
            }
          });
        }
      }
    }
  },
  methods: {
    isExpanded(key) {
      return !!this.expandedGroups[key];
    },
    toggleGroup(key) {
      this.$set(this.expandedGroups, key, !this.expandedGroups[key]);
    },
    onClickGroup(key) {
      if (this.active === '*') return
      this.$emit('click', {key:key,value:'*'})
    },
    onClickPermission(key) {
      if (this.active === '*') return
      this.$emit('click', {key:key,value:true})
    },
    onClickSub(event) {
      if (this.active === '*') return
      this.$emit('click', event)
    },
    buildBreadcrumb(currentLabel) {
      if (!this.parentBreadcrumb) {
        return currentLabel
      }
      return `${this.parentBreadcrumb} > ${currentLabel}`
    },
    matchesSearch(label, key) {
      if (!this.searchFilter) return true
      const searchTerm = this.searchFilter.toLowerCase()
      return (
        label.toLowerCase().includes(searchTerm) ||
        key.toLowerCase().includes(searchTerm)
      )
    },
    hasMatchingChildren(permissions, prefix = '') {
      if (!this.searchFilter) return false
      
      for (const [key, value] of Object.entries(permissions)) {
        const label = this.getLabel(key)
        if (this.matchesSearch(label, key)) return true
        
        if (typeof value === 'object' && value !== null) {
          if (this.hasMatchingChildren(value, `${prefix}${key}.`)) return true
        }
      }
      return false
    },
    getLabel(key) {
      const full_key = `${this.prefix}${key}`
      const label = this.$pgettext('permissions.permission', full_key)
      if (label !== full_key)
        return label
      return key.replace(/_/g, ' ').replace(/\b\w/, l => l.toUpperCase())
    },
    getIcon(key) {
      if (!this.active)
        return {icon: 'cipCircle', class: 'off'}
      if (this.active === '*')
        return {icon: 'cipCheck', class: 'partial'}
      if (this.active[key] === true || this.active[key] === '*')
        return {icon: 'cipCheck', class: 'on'}
      
      // Check if any child has permissions
      if (typeof this.active[key] === 'object' && this.active[key] !== null) {
        if (this.hasActiveChildren(this.active[key])) {
          return {icon: 'cipCheck', class: 'partial'}
        }
      }
      
      return {icon: 'cipCircle', class: 'off'}
    },
    hasActiveChildren(obj) {
      if (!obj || typeof obj !== 'object') return false
      
      for (const [key, value] of Object.entries(obj)) {
        if (value === true || value === '*') return true
        if (typeof value === 'object' && value !== null) {
          if (this.hasActiveChildren(value)) return true
        }
      }
      return false
    }
  }
}
</script>

<style scoped>
/* Layout principale per le righe dei permessi */
.permission-group-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.2rem;
}

/* Pulsante per espandere/comprimere */
.permission-expand-button {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
}

.permission-expand-button:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.permission-expand-button:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Icona del toggle */
.toggle-icon {
  color: #64748b;
  transition: color 0.2s ease-in-out;
}

.permission-expand-button:hover .toggle-icon {
  color: #334155;
}

/* Spazio per allineare i permessi senza pulsante di espansione */
.permission-expand-spacer {
  width: 1.75rem;
  flex-shrink: 0;
}

/* Pulsante principale del permesso - definito più sotto */

.permission-item-button:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.permission-item-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Indicatore di stato del permesso */
.permission-status-indicator {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.2s ease-in-out;
  border: 2px solid #e2e8f0;
}

/* Stati dell'indicatore */
.permission-status-indicator.off {
  background: white;
  border-color: #cbd5e1;
}

.permission-status-indicator.on {
  background: #10b981;
  border-color: #10b981;
  position: relative;
}

.permission-status-indicator.on::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
}

.permission-status-indicator.partial {
  background: linear-gradient(90deg, #10b981 50%, white 50%);
  border-color: #10b981;
  position: relative;
  overflow: hidden;
}

.permission-status-indicator.partial::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 25%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 0.65rem;
  font-weight: bold;
  line-height: 1;
}

/* Contenuto principale del pulsante */
.permission-item-button {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  background: white;
  text-align: left;
  transition: all 0.2s ease-in-out;
  min-width: 0;
}

.permission-item-button > .permission-status-indicator {
  flex-shrink: 0;
}

.permission-item-button > .permission-label {
  flex: 1;
  min-width: 0;
}

.permission-item-button > .permission-breadcrumb {
  flex-shrink: 0;
}

/* Label del permesso */
.permission-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.25rem;
  transition: all 0.2s ease-in-out;
}

.permission-label.is-highlighted {
  background: linear-gradient(90deg, #fef3c7, #fed7aa);
  color: #92400e;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.permission-item-button:disabled .permission-label {
  color: #9ca3af;
}

.permission-item-button:disabled .permission-label.is-highlighted {
  background: #f3f4f6;
  color: #9ca3af;
}

/* Breadcrumb del percorso */
.permission-breadcrumb {
  font-size: 0.6875rem;
  color: #c4c9d0;
  font-weight: 300;
  white-space: nowrap;
  margin-left: auto;
  opacity: 0.7;
  flex-shrink: 0;
  max-width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.permission-item-button:disabled .permission-breadcrumb {
  color: #e5e7eb;
  opacity: 0.5;
}

.permission-item-button:hover .permission-breadcrumb {
  opacity: 1;
  color: #9ca3af;
}

/* Contenitore per i sub-items */
.permission-sub-items {
  margin-left: 2rem;
  margin-top: 0.25rem;
  padding-left: 0.375rem;
  border-left: 1px solid #f1f5f9;
}

/* Responsive per schermi piccoli */
@media (max-width: 640px) {
  .permission-group-row {
    gap: 0.2rem;
  }
  
  .permission-expand-button {
    width: 1.5rem;
    height: 1.5rem;
    min-width: 1.5rem;
  }
  
  .permission-expand-spacer {
    width: 1.5rem;
  }
  
  .permission-item-button {
    padding: 0.25rem 0.375rem;
    gap: 0.25rem;
  }
  
  .permission-sub-items {
    margin-left: 1.5rem;
    padding-left: 0.25rem;
  }
  
  .permission-breadcrumb {
    font-size: 0.625rem;
    max-width: 35%;
  }
}
</style>